import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Space, Typography } from 'antd';
import { PauseCircleOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { fetchSignedMediaUrl } from '../utils/media';
import AudioWaveform from './AudioWaveform';

const { Text } = Typography;

type SegmentClip = {
  audioId: string;
  startSec: number;
  endSec: number;
};

interface GroupedSegmentAudioPlayerProps {
  deviceId: string;
  clips: SegmentClip[];
  inline?: boolean;
  label?: string;
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const GroupedSegmentAudioPlayer: React.FC<GroupedSegmentAudioPlayerProps> = ({
  deviceId,
  clips,
  inline = false,
  label,
}) => {
  const [audioUrls, setAudioUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [shouldLoadAudio, setShouldLoadAudio] = useState(false);
  const [showWaveform, setShowWaveform] = useState(false);
  const [activeClipIndex, setActiveClipIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const pendingAutoPlayRef = useRef(false);

  const normalizedClips = useMemo(
    () => clips
      .map((clip) => ({
        audioId: clip.audioId,
        startSec: Math.max(0, clip.startSec),
        endSec: Math.max(clip.startSec, clip.endSec),
      }))
      .filter((clip) => clip.audioId && clip.endSec > clip.startSec),
    [clips],
  );
  const activeClip = normalizedClips[activeClipIndex];
  const totalDuration = useMemo(
    () => normalizedClips.reduce((sum, clip) => sum + (clip.endSec - clip.startSec), 0),
    [normalizedClips],
  );
  const activeUrl = activeClip ? audioUrls[activeClip.audioId] : undefined;

  useEffect(() => {
    if (!shouldLoadAudio) {
      return;
    }
    let cancelled = false;
    const loadUrls = async () => {
      if (normalizedClips.length === 0) {
        setAudioUrls({});
        return;
      }
      setLoading(true);
      try {
        const { fetchAuthSession } = await import('../auth/gateway');
        const session = await fetchAuthSession();
        const token = session.tokens?.accessToken?.toString();
        if (!token) {
          return;
        }

        const uniqueAudioIds = Array.from(new Set(normalizedClips.map((clip) => clip.audioId)));
        const entries = await Promise.all(
          uniqueAudioIds.map(async (audioId) => {
            const signedUrl = await fetchSignedMediaUrl(
              `/api/v1/devices/${deviceId}/audio/${audioId}/file`,
              token,
            );
            return [audioId, signedUrl] as const;
          }),
        );

        if (!cancelled) {
          setAudioUrls(Object.fromEntries(entries));
        }
      } catch (error) {
        console.error('Failed to load grouped segment audio urls', error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    setActiveClipIndex(0);
    setIsPlaying(false);
    setCurrentTime(0);
    setAudioDuration(0);
    pendingAutoPlayRef.current = false;
    void loadUrls();
    return () => {
      cancelled = true;
    };
  }, [deviceId, normalizedClips, shouldLoadAudio]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !activeClip) {
      return;
    }

    const handleLoadedMetadata = () => {
      const initialTime = clamp(activeClip.startSec, 0, Math.max(activeClip.startSec, audio.duration || activeClip.endSec));
      setAudioDuration(audio.duration || activeClip.endSec);
      audio.currentTime = initialTime;
      setCurrentTime(initialTime);
      if (pendingAutoPlayRef.current) {
        audio.play()
          .then(() => setIsPlaying(true))
          .catch((error) => console.error('Grouped segment playback error', error))
          .finally(() => {
            pendingAutoPlayRef.current = false;
          });
      }
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      if (audio.currentTime >= activeClip.endSec) {
        audio.pause();
        if (activeClipIndex < normalizedClips.length - 1) {
          pendingAutoPlayRef.current = true;
          setActiveClipIndex((prev) => prev + 1);
          return;
        }
        audio.currentTime = activeClip.startSec;
        setCurrentTime(activeClip.startSec);
        setIsPlaying(false);
      }
    };

    const handleEnded = () => {
      if (activeClipIndex < normalizedClips.length - 1) {
        pendingAutoPlayRef.current = true;
        setActiveClipIndex((prev) => prev + 1);
        return;
      }
      setCurrentTime(activeClip.startSec);
      setIsPlaying(false);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [activeClip, activeClipIndex, normalizedClips.length]);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || !activeClip) {
      return;
    }
    if (!activeUrl) {
      pendingAutoPlayRef.current = true;
      setShouldLoadAudio(true);
      return;
    }
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      return;
    }
    const currentTime = audio.currentTime;
    if (currentTime < activeClip.startSec || currentTime >= activeClip.endSec) {
      audio.currentTime = activeClip.startSec;
      setCurrentTime(activeClip.startSec);
    }
    try {
      await audio.play();
      setIsPlaying(true);
    } catch (error) {
      console.error('Grouped segment playback error', error);
    }
  }, [activeClip, activeUrl, isPlaying]);

  const toggleWaveform = useCallback(() => {
    setShouldLoadAudio(true);
    setShowWaveform((prev) => !prev);
  }, []);

  const handleWaveformSeek = useCallback((nextTime: number) => {
    const audio = audioRef.current;
    if (!audio || !activeClip) {
      return;
    }
    const clampedTime = clamp(nextTime, activeClip.startSec, activeClip.endSec);
    audio.currentTime = clampedTime;
    setCurrentTime(clampedTime);
  }, [activeClip]);

  const formatTime = (seconds: number) => {
    if (!Number.isFinite(seconds)) return '--:--';
    const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
    const ss = String(Math.floor(seconds % 60)).padStart(2, '0');
    return `${mm}:${ss}`;
  };

  const content = (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      <Space size={8} wrap>
        <Button
          size="small"
          type="primary"
          shape="circle"
          icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
          onClick={togglePlay}
          loading={loading}
          disabled={!activeUrl || normalizedClips.length === 0}
        />
        <Text type="secondary">
          {label ? `${label} ` : ''}
          {normalizedClips.length}区間 / 約 {totalDuration.toFixed(1)} 秒
        </Text>
        <Button
          size="small"
          onClick={toggleWaveform}
          disabled={normalizedClips.length === 0}
        >
          {showWaveform ? '波形を隠す' : '波形を表示'}
        </Button>
      </Space>
      {showWaveform && activeUrl && activeClip ? (
        <AudioWaveform
          audioUrl={activeUrl}
          currentTime={currentTime}
          duration={audioDuration}
          onSeek={handleWaveformSeek}
          clipStartSec={activeClip.startSec}
          clipEndSec={activeClip.endSec}
        />
      ) : null}
    </Space>
  );

  return (
    <div style={{ width: '100%' }}>
      <audio ref={audioRef} src={activeUrl || undefined} preload="metadata" />
      {inline ? content : <div>{content}</div>}
    </div>
  );
};

export default GroupedSegmentAudioPlayer;
